import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import clsx from "clsx";
import { ArrowLeft } from "lucide-react";
import { FilterChips, type FilterChipItem } from "../components/FilterChips";
import { Button } from "../components/Button";
import { Label } from "../components/Misc";
import { ToneMarker } from "../status/StatusBadge";
import type { Tone } from "../status/statusMap";
import { useT } from "../i18n";

export type InboxGroup = "blocked" | "attention" | "later";
export interface InboxItem { id: string; group: InboxGroup; type: string; title: ReactNode; context?: ReactNode; age?: ReactNode; tone?: Tone }

const GROUPS: { id: InboxGroup; tone: Tone }[] = [{ id: "blocked", tone: "bad" }, { id: "attention", tone: "warn" }, { id: "later", tone: "neutral" }];

/**
 * Pattern 5 – Inbox (§4.5). The heading answers "what do I need to do" (count + the most important thing);
 * source chips filter by module; items are grouped Blocked → Needs attention → Due later, oldest first (the
 * caller orders items within a group); each item shows type, a plain title, one context line and age/due.
 * The detail pane (`renderDetail`) says what happened, what it affects, the usual fix and the action; acting
 * should select the next item. Keyboard: J/K move, Enter opens. On phones the pane is its own screen with Back.
 * `healthy` summarises the good news in one line.
 */
export function Inbox({ heading, subheading, aside, sources, items, selectedId, onSelect, renderDetail, healthy, emptyState, className }: {
  heading: ReactNode; subheading?: ReactNode;
  /** Compact summary between the heading and the filters (e.g. the Health card on tenant Home). */
  aside?: ReactNode;
  sources?: { items: FilterChipItem[]; value: string; onChange: (id: string) => void };
  items: InboxItem[]; selectedId: string | null; onSelect: (id: string | null) => void; renderDetail: (item: InboxItem) => ReactNode; healthy?: ReactNode; emptyState?: ReactNode; className?: string;
}) {
  const t = useT();
  const ordered = GROUPS.flatMap((g) => items.filter((i) => i.group === g.id));
  const [cursor, setCursor] = useState<number>(() => Math.max(0, ordered.findIndex((i) => i.id === selectedId)));
  const listRef = useRef<HTMLDivElement>(null);
  const selected = ordered.find((i) => i.id === selectedId) ?? null;

  useEffect(() => {
    const idx = ordered.findIndex((i) => i.id === selectedId);
    if (idx >= 0) setCursor(idx);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onKey = (e: KeyboardEvent) => {
    if (ordered.length === 0) return;
    if (e.key === "j" || e.key === "J" || e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(ordered.length - 1, c + 1)); }
    else if (e.key === "k" || e.key === "K" || e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); const it = ordered[cursor]; if (it) onSelect(it.id); }
  };
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${cursor}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  const pane = selected ? renderDetail(selected) : null;
  return (
    <div className={clsx("flex min-h-0 flex-1 flex-col gap-4", className)}>
      <div className={clsx(selected && "hidden md:block")}>
        <h1 className="text-h1">{heading}</h1>
        {subheading && <p className="text-fg-3">{subheading}</p>}
      </div>
      {aside && <div className={clsx(selected && "hidden md:block")}>{aside}</div>}
      {sources && sources.items.length > 1 && <div className={clsx(selected && "hidden md:block")}><FilterChips items={sources.items} value={sources.value} onChange={sources.onChange} ariaLabel="Source" /></div>}
      <div className="flex min-h-0 flex-1 gap-4">
        <div ref={listRef} className={clsx("iga-card w-full flex-none overflow-y-auto focus-ring md:w-[460px] lg:w-[500px]", selected && "hidden md:block")} tabIndex={0} role="listbox" aria-label="Work queue" aria-activedescendant={ordered[cursor] ? `inbox-${ordered[cursor]!.id}` : undefined} onKeyDown={onKey}>
          {ordered.length === 0 && (emptyState ?? <div className="p-6 text-fg-3">{t("ui.nothingHereYet")}</div>)}
          {GROUPS.map((g) => {
            const list = items.filter((i) => i.group === g.id);
            if (list.length === 0) return null;
            return (
              <div key={g.id} role="group" aria-label={t(`ui.group.${g.id}`)}>
                <div className="border-b border-line bg-surface-2 px-4 pb-1.5 pt-2.5"><Label>{t(`ui.group.${g.id}`)} · {list.length}</Label></div>
                {list.map((it) => {
                  const idx = ordered.indexOf(it);
                  const tone = it.tone ?? g.tone;
                  const isSel = it.id === selectedId;
                  return (
                    <div key={it.id} id={`inbox-${it.id}`} data-index={idx} role="option" aria-selected={isSel} onClick={() => onSelect(it.id)}
                      className={clsx("flex cursor-pointer items-start gap-3 border-b border-line px-4 py-3 last:border-b-0", isSel ? "bg-brand-soft" : idx === cursor ? "bg-surface-2" : "hover:bg-surface-2")}>
                      <span className={clsx("mt-[3px] inline-flex h-5 w-5 flex-none items-center justify-center rounded-full", `iga-tone-${tone}`)}><ToneMarker tone={tone} /></span>
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="text-xs font-semibold text-fg-3">{it.type}</span>
                        <span className="font-semibold">{it.title}</span>
                        {it.context && <span className="text-small text-fg-3">{it.context}</span>}
                      </div>
                      {it.age && <span className="flex-none text-[12.5px] text-fg-3">{it.age}</span>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className={clsx("iga-card min-w-0 flex-1 flex-col p-5 md:flex", selected ? "flex" : "hidden")}>
          {selected && <div className="mb-3 md:hidden"><Button variant="ghost" size="touch" icon={<ArrowLeft className="h-4 w-4" aria-hidden="true" />} onClick={() => onSelect(null)}>{t("ui.back")}</Button></div>}
          {pane ?? <div className="text-fg-3">Select an item to see what happened and what to do.</div>}
          {healthy && <div className="mt-auto pt-6 text-fg-3">{healthy}</div>}
        </div>
      </div>
    </div>
  );
}

/** Detail pane body: type, title, status, what happened / affects, the usual fix, and the actions. */
export function InboxDetail({ type, title, status, children, facts, fix, actions }: { type: ReactNode; title: ReactNode; status?: ReactNode; children?: ReactNode; facts?: ReactNode; fix?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <span className="text-xs font-semibold text-fg-3">{type}</span>
      <h2 className="-mt-2 text-[20px] font-bold leading-7">{title}</h2>
      {status && <div>{status}</div>}
      {children && <div className="text-fg-2">{children}</div>}
      {facts}
      {fix && <div className="flex flex-col gap-1.5 rounded-control bg-surface-2 p-3.5"><b>Usual fixes</b><span className="text-fg-2">{fix}</span></div>}
      {actions && <div className="flex flex-wrap gap-2.5">{actions}</div>}
    </div>
  );
}
